/**
 * Centralized API endpoints constants for the Carvy application
 * 
 * This file contains all API endpoint paths used throughout the application.
 * All endpoints are relative to the API_BASE_URL defined in routes.constants.js
 * 
 * Usage:
 *   import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';
 *   apiClient.get(API_ENDPOINTS.AUTH.LOGIN)
 */

export const API_ENDPOINTS = {
  // ============================================
  // AUTHENTICATION ENDPOINTS
  // ============================================
  AUTH: {
    BASE: '/auth',
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    GOOGLE: '/auth/google',
  },

  // ============================================
  // VEHICLE ENDPOINTS
  // ============================================
  VEHICLES: {
    BASE: '/vehicles',
    LIST: '/vehicles',
    GET_BY_ID: (id) => `/vehicles/${id}`,
    CREATE: '/vehicles',
    UPDATE: (id) => `/vehicles/${id}`,
    DELETE: (id) => `/vehicles/${id}`,
    TOGGLE_ACTIVE: (id) => `/vehicles/${id}/toggle-active`,
    TOGGLE_VERIFIED: (id) => `/vehicles/${id}/toggle-verified`,
    TOGGLE_FEATURED: (id) => `/vehicles/${id}/toggle-featured`,
    BLOCK_DATES: (id) => `/vehicles/${id}/block-dates`,
    GET_BLOCKED_DATES: (id) => `/vehicles/${id}/blocked-dates`,
    DELETE_BLOCKED_DATES: (id) => `/vehicles/${id}/block-dates`,
    HISTORY: (id) => `/vehicles/${id}/history`,
  },

  // ============================================
  // ADMIN - VEHICLES MANAGEMENT
  // ============================================
  ADMIN_VEHICLES: {
    BASE: '/admin/vehicles',
    REJECT: (id) => `/admin/vehicles/${id}/reject`,
    RESET_REJECTION: (id) => `/admin/vehicles/${id}/reset-rejection`,
    REJECT_DELETION: (id) => `/admin/vehicles/${id}/reject-deletion`,
    DELETE_PERMANENT: (id) => `/admin/vehicles/${id}/delete-permanent`,
    DELETE_WITH_LOGS: (id) => `/admin/vehicles/${id}/delete-with-logs`,
  },

  // ============================================
  // ADMIN - VEHICLE FEATURES
  // ============================================
  VEHICLE_FEATURES: {
    BASE: '/admin/vehicle-features',
    LIST: '/admin/vehicle-features',
    GET_BY_ID: (id) => `/admin/vehicle-features/${id}`,
    CREATE: '/admin/vehicle-features',
    UPDATE: (id) => `/admin/vehicle-features/${id}`,
    DELETE: (id) => `/admin/vehicle-features/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-features',
    TOGGLE_STATUS: (id) => `/admin/vehicle-features/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE LOCATIONS
  // ============================================
  VEHICLE_LOCATIONS: {
    BASE: '/admin/vehicle-locations',
    LIST: '/admin/vehicle-locations',
    GET_BY_ID: (id) => `/admin/vehicle-locations/${id}`,
    CREATE: '/admin/vehicle-locations',
    UPDATE: (id) => `/admin/vehicle-locations/${id}`,
    DELETE: (id) => `/admin/vehicle-locations/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-locations',
    TOGGLE_STATUS: (id) => `/admin/vehicle-locations/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE MAKES (Brands)
  // ============================================
  VEHICLE_MAKES: {
    BASE: '/admin/vehicle-makes',
    LIST: '/admin/vehicle-makes',
    GET_BY_ID: (id) => `/admin/vehicle-makes/${id}`,
    CREATE: '/admin/vehicle-makes',
    UPDATE: (id) => `/admin/vehicle-makes/${id}`,
    DELETE: (id) => `/admin/vehicle-makes/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-makes',
    TOGGLE_STATUS: (id) => `/admin/vehicle-makes/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE MODELS
  // ============================================
  VEHICLE_MODELS: {
    BASE: '/admin/vehicle-models',
    LIST: '/admin/vehicle-models',
    GET_BY_ID: (id) => `/admin/vehicle-models/${id}`,
    CREATE: '/admin/vehicle-models',
    UPDATE: (id) => `/admin/vehicle-models/${id}`,
    DELETE: (id) => `/admin/vehicle-models/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-models',
    TOGGLE_STATUS: (id) => `/admin/vehicle-models/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE ODOMETERS
  // ============================================
  VEHICLE_ODOMETERS: {
    BASE: '/admin/vehicle-odometers',
    LIST: '/admin/vehicle-odometers',
    GET_BY_ID: (id) => `/admin/vehicle-odometers/${id}`,
    CREATE: '/admin/vehicle-odometers',
    UPDATE: (id) => `/admin/vehicle-odometers/${id}`,
    DELETE: (id) => `/admin/vehicle-odometers/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-odometers',
    TOGGLE_STATUS: (id) => `/admin/vehicle-odometers/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE FUEL TYPES
  // ============================================
  VEHICLE_FUEL_TYPES: {
    BASE: '/admin/vehicle-fuel-types',
    LIST: '/admin/vehicle-fuel-types',
    GET_BY_ID: (id) => `/admin/vehicle-fuel-types/${id}`,
    CREATE: '/admin/vehicle-fuel-types',
    UPDATE: (id) => `/admin/vehicle-fuel-types/${id}`,
    DELETE: (id) => `/admin/vehicle-fuel-types/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-fuel-types',
    TOGGLE_STATUS: (id) => `/admin/vehicle-fuel-types/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE TYPES
  // ============================================
  VEHICLE_TYPES: {
    BASE: '/admin/vehicle-types',
    LIST: '/admin/vehicle-types',
    GET_BY_ID: (id) => `/admin/vehicle-types/${id}`,
    CREATE: '/admin/vehicle-types',
    UPDATE: (id) => `/admin/vehicle-types/${id}`,
    DELETE: (id) => `/admin/vehicle-types/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-types',
    TOGGLE_STATUS: (id) => `/admin/vehicle-types/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE RULES
  // ============================================
  VEHICLE_RULES: {
    BASE: '/admin/vehicle-rules',
    LIST: '/admin/vehicle-rules',
    GET_BY_ID: (id) => `/admin/vehicle-rules/${id}`,
    CREATE: '/admin/vehicle-rules',
    UPDATE: (id) => `/admin/vehicle-rules/${id}`,
    DELETE: (id) => `/admin/vehicle-rules/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-rules',
    TOGGLE_STATUS: (id) => `/admin/vehicle-rules/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE CANCELLATION POLICIES
  // ============================================
  VEHICLE_CANCELLATION_POLICIES: {
    BASE: '/admin/vehicle-cancellation-policies',
    LIST: '/admin/vehicle-cancellation-policies',
    GET_BY_ID: (id) => `/admin/vehicle-cancellation-policies/${id}`,
    CREATE: '/admin/vehicle-cancellation-policies',
    UPDATE: (id) => `/admin/vehicle-cancellation-policies/${id}`,
    DELETE: (id) => `/admin/vehicle-cancellation-policies/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-cancellation-policies',
    TOGGLE_STATUS: (id) => `/admin/vehicle-cancellation-policies/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - VEHICLE CANCELLATION REASONS
  // ============================================
  VEHICLE_CANCELLATION_REASONS: {
    BASE: '/admin/vehicle-cancellation-reasons',
    LIST: '/admin/vehicle-cancellation-reasons',
    GET_BY_ID: (id) => `/admin/vehicle-cancellation-reasons/${id}`,
    CREATE: '/admin/vehicle-cancellation-reasons',
    UPDATE: (id) => `/admin/vehicle-cancellation-reasons/${id}`,
    DELETE: (id) => `/admin/vehicle-cancellation-reasons/${id}`,
    DELETE_MULTIPLE: '/admin/vehicle-cancellation-reasons',
    TOGGLE_STATUS: (id) => `/admin/vehicle-cancellation-reasons/${id}/toggle-status`,
  },

  REJECTION_REASONS: {
    BASE: '/admin/rejection-reasons',
    LIST: '/admin/rejection-reasons',
    GET_BY_ID: (id) => `/admin/rejection-reasons/${id}`,
    CREATE: '/admin/rejection-reasons',
    UPDATE: (id) => `/admin/rejection-reasons/${id}`,
    DELETE: (id) => `/admin/rejection-reasons/${id}`,
    DELETE_MULTIPLE: '/admin/rejection-reasons',
    TOGGLE_STATUS: (id) => `/admin/rejection-reasons/${id}/toggle-status`,
  },

  // ============================================
  // ADMIN - BOOKINGS
  // ============================================
  BOOKINGS: {
    BASE: '/admin/bookings',
    LIST: '/admin/bookings',
    GET_BY_ID: (id) => `/admin/bookings/${id}`,
    UPDATE_STATUS: (id) => `/admin/bookings/${id}/status`,
    UPDATE_PAYMENT_STATUS: (id) => `/admin/bookings/${id}/payment-status`,
    UPDATE_DATES: (id) => `/admin/bookings/${id}/dates`,
    GET_FINANCIALS: (id) => `/admin/bookings/${id}/financials`,
    REGENERATE_OTP: (id) => `/admin/bookings/${id}/regenerate-otp`,
    STATS: '/admin/bookings/stats',
    DELETE: (id) => `/admin/bookings/${id}`,
    BULK: '/admin/bookings/bulk',
  },

  // ============================================
  // USER BOOKINGS (Public)
  // ============================================
  USER_BOOKINGS: {
    BASE: '/bookings',
    CREATE: '/bookings',
  },

  // ============================================
  // VENDOR BOOKINGS (Agency)
  // ============================================
  VENDOR_BOOKINGS: {
    BASE: '/vendor/bookings',
    LIST: '/vendor/bookings',
  },

  // ============================================
  // ADMIN - USERS
  // ============================================
  ADMIN_USERS: {
    BASE: '/admin/users',
    KYC: '/admin/users/kyc',
    GET_BY_ID: (id) => `/admin/users/${id}`,
    VERIFY_CONTACT: '/admin/users/verify-contact',
    UPDATE_ROLE: (id) => `/admin/users/${id}/role`,
    UPDATE_STATUS: (id) => `/admin/users/${id}/status`,
    UPDATE_PERMISSIONS: (id) => `/admin/users/${id}/permissions`,
    DELETE: (id) => `/admin/users/${id}`,
  },

  // ============================================
  // ADMIN - AUDIT LOGS
  // ============================================
  ADMIN_AUDIT_LOGS: {
    LIST: '/admin/audit-logs',
    STATS: '/admin/audit-logs/stats',
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  NOTIFICATIONS: {
    BASE: '/notifications',
    LIST: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_AS_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_AS_READ: '/notifications/read-all',
    DELETE: (id) => `/notifications/${id}`,
  },

  // ============================================
  // UPLOAD ENDPOINTS
  // ============================================
  UPLOAD: {
    BASE: '/upload',
    SINGLE: '/upload',
    MULTIPLE: '/upload/multiple',
    IMAGES: '/upload/images',
    DOCUMENTS: '/upload/documents',
  },

  // ============================================
  // KYC ENDPOINTS
  // ============================================
  KYC: {
    BASE: '/user/kyc',
    SUBMIT: '/user/kyc/submit',
    STATUS: '/user/kyc/status',
    VERIFY: (userId) => `/admin/kyc/${userId}/verify`,
    REJECT: (userId) => `/admin/kyc/${userId}/reject`,
  },

  // ============================================
  // VEHICLE REFERENCE DATA (Public - for vendors and admins)
  // ============================================
  VEHICLE_REFERENCE: {
    BASE: '/vehicle-reference',
    MAKES: '/vehicle-reference/makes',
    MAKE_BY_ID: (id) => `/vehicle-reference/makes/${id}`,
    MODELS: '/vehicle-reference/models',
    MODEL_BY_ID: (id) => `/vehicle-reference/models/${id}`,
    FUEL_TYPES: '/vehicle-reference/fuel-types',
    FUEL_TYPE_BY_ID: (id) => `/vehicle-reference/fuel-types/${id}`,
    ODOMETERS: '/vehicle-reference/odometers',
    ODOMETER_BY_ID: (id) => `/vehicle-reference/odometers/${id}`,
    FEATURES: '/vehicle-reference/features',
    FEATURE_BY_ID: (id) => `/vehicle-reference/features/${id}`,
    LOCATIONS: '/vehicle-reference/locations',
    LOCATION_BY_ID: (id) => `/vehicle-reference/locations/${id}`,
    RULES: '/vehicle-reference/rules',
    RULE_BY_ID: (id) => `/vehicle-reference/rules/${id}`,
    CANCELLATION_POLICIES: '/vehicle-reference/cancellation-policies',
    CANCELLATION_POLICY_BY_ID: (id) => `/vehicle-reference/cancellation-policies/${id}`,
  },

  // ============================================
  // V1 API (Public - for Flutter and vendors)
  // ============================================
  V1: {
    GET_ALL_CATEGORIES: '/v1/get-all-categories',
  },

  // ============================================
  // ADMIN - PAYMENT METHODS
  // ============================================
  PAYMENT_METHODS: {
    BASE: '/admin/payment-methods',
    LIST: '/admin/payment-methods',
    GET_BY_ID: (id) => `/admin/payment-methods/${id}`,
    CREATE: '/admin/payment-methods',
    UPDATE: (id) => `/admin/payment-methods/${id}`,
    DELETE: (id) => `/admin/payment-methods/${id}`,
    TOGGLE_STATUS: (id) => `/admin/payment-methods/${id}/toggle-status`,
  },
};

/**
 * Helper function to build full API URL
 * @param {string} endpoint - API endpoint (e.g., '/auth/register')
 * @returns {string} Full API URL
 */
export const buildApiEndpoint = (endpoint) => {
  // [REF: CONFIG-001] Configuration de l'URL de l'API
  // Mode DÉVELOPPEMENT (actif) : utilise localhost pour le développement local
  const baseUrl = process.env.REACT_APP_API_URL || 'https://carvy.tech/api';
  // [REF: CONFIG-002] Mode PRODUCTION (commenté) : décommentez pour la production
  // const baseUrl = process.env.REACT_APP_API_URL || 'http://72.62.25.49:5000/api';
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

export default API_ENDPOINTS;

